import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { type SelectChangeEvent } from '@mui/material';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { type ColumnFiltersState, type Updater } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { eventsAtom } from 'atoms/events';
import LinkButton from 'components/buttons/LinkButton';
import SearchInput from 'components/inputs/searchInput/SearchInput';
import { GenderSelector } from 'components/selectors/GenderSelector';
import TMXSelect from 'components/selectors/tmxSelector/TMXSelect';
import { SelectItemLabel } from 'components/typography/Typography';
import { NO_SELECTION } from 'constants/generic';

import { PlayerStatusOptions } from './utils';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface PlayersFiltersProps {
  isSearchAutoFocus?: boolean;
  setGlobalFilter: (value: string) => void;
  setFilters: (updater: Updater<ColumnFiltersState>) => void;
  filters: ColumnFiltersState;
  globalFilter: string;
  genderOptions?: any[];
  showFilters?: string[];
}

const PlayersFilters: React.FC<React.PropsWithChildren<PlayersFiltersProps>> = ({
  isSearchAutoFocus,
  setGlobalFilter,
  setFilters,
  filters,
  globalFilter,
  genderOptions,
  showFilters = ['search', 'gender', 'events', 'signedIn', 'status'],
}) => {
  const { t } = useTranslation();
  const events = useAtomValue(eventsAtom);

  const searchRef = useRef<HTMLInputElement>(null);

  const handleResetGlobalFilter = useCallback(() => {
    setGlobalFilter('');
    searchRef.current.value = '';
  }, [setGlobalFilter]);

  const handleUpdateFilters = useCallback(
    (id: string, value: string) => {
      const filterIndex = filters.findIndex((filter) => filter.id === id);
      const updatedFilters = [...filters];
      if (filterIndex !== -1) updatedFilters[filterIndex] = { id, value };
      else updatedFilters.push({ id, value });
      setFilters(updatedFilters);
    },
    [filters, setFilters],
  );

  const handleResetFilters = useCallback(() => {
    handleResetGlobalFilter();
    setFilters([]);
  }, [handleResetGlobalFilter, setFilters]);

  const signedInFilter = useMemo(
    () => (filters.find((filter) => filter.id === 'signedIn')?.value ?? NO_SELECTION) as string,
    [filters],
  );
  const eventsFilter = useMemo(
    () => (filters.find((filter) => filter.id === 'events')?.value ?? NO_SELECTION) as string,
    [filters],
  );
  const genderFilter = useMemo(
    () => (filters.find((filter) => filter.id === 'gender')?.value ?? NO_SELECTION) as string,
    [filters],
  );
  const statusFilter = useMemo(
    () => (filters.find((filter) => filter.id === 'status')?.value ?? NO_SELECTION) as string,
    [filters],
  );

  const showSearchFilter = useMemo(() => showFilters.find((el) => el === 'search') ?? false, [showFilters]);
  const showGenderFilter = useMemo(() => showFilters.find((el) => el === 'gender') ?? false, [showFilters]);
  const showEventsFilter = useMemo(() => showFilters.find((el) => el === 'events') ?? false, [showFilters]);
  const showSignedInFilter = useMemo(() => showFilters.find((el) => el === 'signedIn') ?? false, [showFilters]);
  const showStatusFilter = useMemo(() => showFilters.find((el) => el === 'status') ?? false, [showFilters]);

  const eventsOptions = useMemo(
    () =>
      events.map((event) => ({
        text: event.eventName,
        value: event.eventId,
      })),
    [events],
  );

  const signedInSelectorOptions = useMemo(
    () => [
      { text: t('signin.signedin'), value: 'signed in' },
      { text: t('signin.notsignedin'), value: 'sign in' },
    ],
    [t],
  );

  const statusSelectorOptions = useMemo(() => {
    let playerStatuses = [
      { text: t('playerStatus.selected'), value: PlayerStatusOptions.SELECTED },
      { text: t('playerStatus.alternate'), value: PlayerStatusOptions.ALTERNATE },
      { text: t('playerStatus.unpaired'), value: PlayerStatusOptions.UNPAIRED },
      { text: t('playerStatus.withdrawn'), value: PlayerStatusOptions.WITHDRAWN },
    ];

    if (events?.every((e) => e.eventType === EventTypeEnum.SINGLES)) {
      playerStatuses = playerStatuses.filter((status) => status.value !== PlayerStatusOptions.UNPAIRED);
    } else {
      if (eventsFilter !== NO_SELECTION) {
        if (events?.filter((e) => e.eventId === eventsFilter)[0]?.eventType === EventTypeEnum.SINGLES) {
          playerStatuses = playerStatuses.filter((status) => status.value !== PlayerStatusOptions.UNPAIRED);
        }
      }
    }

    return playerStatuses;
  }, [t, eventsFilter, events]);

  useEffect(() => {
    if (
      events?.filter((e) => e.eventId === eventsFilter)[0]?.eventType === EventTypeEnum.SINGLES &&
      statusFilter === PlayerStatusOptions.UNPAIRED
    ) {
      handleUpdateFilters('status', undefined);
    }
  }, [eventsFilter, statusFilter, handleUpdateFilters, events]);

  const filtersApplied = !!(filters.length || globalFilter);

  return (
    <Grid container direction="row">
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Grid container alignItems="center" direction="row" justifyContent="flex-start" spacing={1}>
          {!!showSearchFilter && (
            <Grid item sm="auto" xs={12}>
              <SearchInput
                fullWidth
                autoFocus={isSearchAutoFocus}
                inputRef={searchRef}
                placeholder="Search"
                onChange={(event) => setGlobalFilter(event.target.value)}
                onClear={handleResetGlobalFilter}
              />
            </Grid>
          )}
          {!!showEventsFilter && (
            <Grid item sm="auto" xs={12}>
              <TMXSelect
                value={eventsFilter}
                onChange={(event) =>
                  handleUpdateFilters('events', event.target.value === NO_SELECTION ? undefined : event.target.value)
                }
              >
                <MenuItem value={NO_SELECTION}>
                  <SelectItemLabel>{t('schedule.allevents')}</SelectItemLabel>
                </MenuItem>
                {eventsOptions.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    <SelectItemLabel>{t.text}</SelectItemLabel>
                  </MenuItem>
                ))}
              </TMXSelect>
            </Grid>
          )}
          {genderOptions?.length > 1 && !!showGenderFilter && (
            <Grid item sm="auto" xs={12}>
              <GenderSelector
                options={genderOptions}
                selectedGender={genderFilter}
                selectGender={(event) =>
                  handleUpdateFilters('gender', event.target.value === NO_SELECTION ? undefined : event.target.value)
                }
              />
            </Grid>
          )}
          {!!showSignedInFilter && (
            <Grid item sm="auto" xs={12}>
              <TMXSelect
                value={signedInFilter}
                onChange={(event: SelectChangeEvent<string>) =>
                  handleUpdateFilters('signedIn', event.target.value === NO_SELECTION ? undefined : event.target.value)
                }
              >
                <MenuItem value={NO_SELECTION}>
                  <SelectItemLabel>{t('anyCheckIn')}</SelectItemLabel>
                </MenuItem>
                {signedInSelectorOptions.map((t) => (
                  <MenuItem key={t.text} value={t.value}>
                    <SelectItemLabel>{t.text}</SelectItemLabel>
                  </MenuItem>
                ))}
              </TMXSelect>
            </Grid>
          )}
          {!!showStatusFilter && (
            <Grid item sm="auto" xs={12}>
              <TMXSelect
                value={statusFilter}
                onChange={(event) => {
                  handleUpdateFilters('status', event.target.value === NO_SELECTION ? undefined : event.target.value);
                }}
              >
                <MenuItem value={NO_SELECTION}>
                  <SelectItemLabel>{t('playerStatus.allStatuses')}</SelectItemLabel>
                </MenuItem>
                {statusSelectorOptions.map((t) => (
                  <MenuItem key={t.text} value={t.value}>
                    <SelectItemLabel>{t.text}</SelectItemLabel>
                  </MenuItem>
                ))}
              </TMXSelect>
            </Grid>
          )}
          {filtersApplied && (
            <Grid item sm="auto" xs={12}>
              <LinkButton onClick={handleResetFilters}>{t('reset')}</LinkButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PlayersFilters;
